import { createAsyncThunk } from '@reduxjs/toolkit';
import { ChatConnectionDetails } from 'domain/Chat';
import { getApiBaseUrl } from 'modules/api/utils';
import { updateKeycloakToken } from 'modules/keycloak';
import { keycloakService } from 'modules/keycloak/services';
import { ChatTelemetry, chatErrorAttrs, trackChatEvent } from 'pages/EventBroadcast/chatClient/ably/telemetry';
import { selectActiveEventDetails } from 'store/apis/event';
import { eventApiId, toEventApiId } from 'store/apis/event/utils/eventApiId';
import { selectActiveEventId } from 'store/features/event';

import { setChatConnectionDetails } from './chatSlice';

import type { RootState } from 'store';

/**
 * Pull-based fallback: mint a chat grant from the backend when no `chat_connection_details` WS push
 * has arrived. Best-effort — a failure is swallowed so it never disrupts the rest of the join flow.
 */
export const fetchChatConnectionDetails = createAsyncThunk<void, void, { state: RootState }>(
  'chat/fetchConnectionDetails',
  async (_, { dispatch, getState }) => {
    const state = getState();
    // EventApiId (HNS-148): this is a path param on mintEventChatToken, not a WS destination.
    const eventId = toEventApiId(selectActiveEventDetails(state)) ?? eventApiId(undefined, selectActiveEventId(state));
    if (eventId === undefined) return;

    await updateKeycloakToken(keycloakService);
    const startedAt = Date.now();
    // Still swallowed for the join flow (the thunk just rejects), but no longer silent: a 503 means
    // the backend could not reach chat-service, a 404 that the room was never created, and a
    // rejected fetch (offline, DNS, CORS) or unreadable body is recorded with no HTTP status.
    const trackPull = (attrs: Record<string, unknown>) =>
      trackChatEvent(ChatTelemetry.token, {
        chat_event_id: eventId,
        chat_token_source: 'pull',
        chat_duration_ms: Date.now() - startedAt,
        ...attrs,
      });
    let response: Response;
    let details: ChatConnectionDetails;
    try {
      response = await fetch(`${getApiBaseUrl('v1')}/events/${eventId}/chat/token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloakService.token}` },
      });
      if (!response.ok) {
        trackPull({ chat_outcome: 'failure', chat_http_status: response.status });
        return;
      }
      details = (await response.json()) as ChatConnectionDetails;
    } catch (error) {
      trackPull({ chat_outcome: 'failure', ...chatErrorAttrs(error) });
      throw error;
    }
    trackPull({
      chat_outcome: 'success',
      chat_http_status: response.status,
      chat_room_id: details.roomId,
      chat_role: details.role,
    });
    dispatch(setChatConnectionDetails(details));
  }
);
